import React from "react";
import { motion } from "framer-motion";
import "../../About.css";

const sections = [
  {
    title: "G-ETTO",
    desc: `Garetto (G-ETTO) is a redistributor token designed with a
            built-in, autonomous reward mechanism embedded directly into its
            core logic. Unlike traditional tokens that rely on external staking
            systems or manual reward distribution, G-ETTO integrates
            redistribution at the protocol level. Every transaction contributes
            to a continuously operating reward system, creating a dynamic and
            self-sustaining ecosystem which periodically returns value back to
            participants according to predefined on-chain rules.`,
  },
  {
    title: "How It Works",
    desc: `With every transaction, 0.1% of the transferred amount is
            automatically allocated to a dedicated redistribution contract which 
            is integral part of the token. This contract continuously processes 
            incoming activity by generating internal randomized parameters, evaluating 
            predefined conditions, determining whether a redistribution event should occur.
            When the required conditions are met, the system automatically triggers a payout,
            sending a portion of the accumulated reserve directly to the transaction initiator.
            The redistribution mechanism is designed to reward active
            participation introduce unpredictable but deterministic outcomes,
            maintain continuous on-chain engagement. The multi-layered architecture creates a
            continuous balance between frequent micro-redistributions, mid-range reward cycles, and rare large-scale
                 payouts, maintaining persistent on-chain activity, unpredictability, and 
                 autonomous token circulation.`,
  },

  {
    title: "Not Gambling — Deterministic Pure Redistribution",
    desc: `Redistributor token itself is not a gambling system. There are: no bets,
            no centralized entity which is interested in holders loss because it is their profit .
            All redistributions originate exclusively from the protocol-defined allocation of
            transaction volume. The logic is fully deterministic and encoded in
            the smart contract, ensuring transparency and predictability at the
            system level. Participants are not risking assets — instead, they
            are interacting with a system that periodically redistributes value
            back to active users based on predefined mathematical
            conditions. The Garetto ecosystem has its own gambling contracts, and they
            significantly showcase the difference between gambling and redistribution mechanics.`,
  },
  {
    title: "Core Principle",

    desc: (
      <>
        At its core, G-ETTO transforms transactional activity into a continuous
        reward engine, where every transfer contributes to the system, every
        participant indirectly fuels the redistribution pool and active users
        gain exposure to automated reward events over time. Even if you hold a
        large amount of tokens with significant value and you move them from an
        address to another address, you are still required to pay the same fee
        as any other participant. This is part of your contribution to the
        community and helps sustain the system for everyone. If you do not agree
        with this structure, participation in the ecosystem may not be suitable
        for you.
      </>
    ),
  },
  {
    title: "Ecosystem Structure",

    desc: (
      <>
        Garetto is not a single token. Garetto is a group of redistributor
        tokens with an inegrated autonomous redistribution mechanism in their
        structure, deployed across multiple EVM-compatible blockchains. Due to
        their state-dependent architecture, redistributor tokens cannot preserve
        their core mechanics through conventional bridging. Each Garetto token
        operates as an independent market with its own liquidity, price
        discovery, redistribution reserve, trading activity, and community
        participation. Together, these independent markets form the broader
        Garetto ecosystem. This architecture also creates a new decentralized
        trading niche. Instead of concentrating all activity into a single
        bridged asset, independent Garetto markets can emerge across multiple
        blockchains while preserving their native redistribution mechanics. Each
        market develops its own economic characteristics, liquidity profile, and
        participant behavior, creating opportunities for unique forms of
        decentralized trading, liquidity movement, and cross-chain value
        discovery. The result is not merely a token deployed on multiple
        networks, but an ecosystem of independent yet related markets operating
        under a common protocol design.
      </>
    ),
  },
  {
    title: "Summary",
    desc: ` G-ETTO introduces a new class of token mechanics: Embedded
            redistribution logic, multi-layered reward system with fully autonomous
            execution and activity-driven incentives. It is a system where
            engagement not speculation drives participation, and where value
            is redistributed algorithmically back into the ecosystem. In theory
            redistributor tokens can be a perfect payment method when the taxes
            or other payments go directly to their designated
            recipients, such as tax authorities, service providers, or
            redistribution mechanisms.`,
  },
  {
    title: "Decentralized Gambling Tournament Contracts",
    desc: `The Garetto ecosystem includes a collection of open-source tournament smart contracts.
    One of the most distinctive components is the tournament architecture featuring seat buy and 
    seat sell functionality, combining elements of market trading with competitive elimination 
    mechanics. Participants may voluntarily engage with these tournament systems, but the existence
    of tournament contracts does not define G-ETTO as a gambling token. The redistribution protocol
    and tournament ecosystem are separate components operating within the broader Garetto framework.`,
  },
  {
    title: "Security & Development Status",
    desc: (
      <>
        The contracts are currently in a solid operational state and have been
        designed with security, determinism, and transparency. The ecosystem is
        built to maximize decentralization at the technical level and all core
        contracts are open source. Garetto should nevertheless be regarded as an
        experimental financial model exploring new forms of redistribution,
        incentive structures, decentralized competition, and on-chain economic
        interaction. Independent audits are planned and will be conducted by
        multiple external reviewers as part of the ongoing development process.
        Users should perform their own research and understand the mechanics of
        the protocol before participating.
        <br />
        <br />
        All smart contracts are publicly accessible in a{" "}
        <a
          href="https://github.com/lernoh2/RETO/tree/Garetto"
          target="_blank"
          rel="noopener noreferrer"
          className="github-link"
        >
          GitHub repository
        </a>
        .
      </>
    ),
  },
  {
    title: "G-ETTO distribution among holders",
    type: "warning",
    desc: (
      <>
        The total supply of G-ETTO is fixed at 100,000,000 tokens. The entire
        supply is allocated to a dedicated airdrop contract. Each eligible
        wallet receives 50,000 tokens, resulting in distribution across 2,000
        independent addresses. There are no ICO phases, private sales, seed
        rounds, venture capital allocations, insider discounts, presales, or
        privileged token offerings. After distribution is complete, market
        participants alone determine the token's value through open trading and
        market activity.
      </>
    ),
  },
];

export default function About() {
  return (
    <div className="pageContent">
      <div className="aboutBlock">
        <h3>ABOUT</h3>
        <div className="about-container">
          <div className="about-wrapper">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`about-item ${
                  section.type === "warning" ? "warning-block" : ""
                }`}
              >
                <div className="about-content">
                  <h3
                    className={`about-title ${
                      section.type === "warning" ? "important-title" : ""
                    }`}
                  >
                    {section.title}
                  </h3>
                  <p>{section.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
